import { useCallback, useEffect, useRef, useState } from 'react'
import { InputHidden, RectangularInputProps } from '..'
import TwinColor from '../../../baseComponents/TwinColor'
import useOnClickOutside from '../../../utils/hooks/useOnClickOut'
import { CustomRenderColor } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { CustomSelectDropdownIcon } from '../../CustomSelect/DropDown'
import TwinIcon from '../../../baseComponents/TwinIcon'
import { faX } from '@fortawesome/pro-light-svg-icons'

export type InputColorProps = RectangularInputProps  & InputColorLogicProps & {
    colors?: string[]
}

const InputColor: React.FC<InputColorProps> = ({ className, colors, label, onChange, defaultValue, value, ...inputRest}) => {
    const readOnly = inputRest.readOnly
    const { showPicker, handleShowPicker, handleMyColor, myColor, ref } = useInputColorLogic({ onChange, defaultValue, value })
    const borderColor = showPicker ? 'border-green-43' : ''
    const textColor = showPicker ? 'text-green-43' : ''
    return (
        <div className={'relative flex flex-col gap-y-2 ' + (className || '')}>
            <label title={label} className={'cursor-pointer h-19 light12 twin_elipsis'}>{label}{inputRest.required ? <span className='ml-5 text-green-21'>*</span> : ''}</label>
            <div className={`flex items-center cursor-pointer relative border border-gray-D6 ${borderColor} rounded-lg p-5 input_color_header h-41`} onClick={readOnly ? undefined : handleShowPicker}>
                <CustomRenderColor value={myColor} />
                <span className={`ml-10 ${textColor}`}>{myColor}</span>
                <CustomSelectDropdownIcon opened={showPicker} />
            </div>
            <InputHidden value={myColor} {...inputRest}/>
            {showPicker ? <div ref={ref} ><TwinColor className='twinColor' color={myColor} onChange={handleMyColor} {...{ colors }}/></div> : null}
        </div>
    )
}
export type InputColorWithDeleteButtonProps = RectangularInputProps  & InputColorLogicProps & {
    colors?: string[]
}

const InputColorWithDeleteButton: React.FC<InputColorWithDeleteButtonProps> = ({ className, colors, label, onChange, defaultValue, value, ...inputRest}) => {
    const readOnly = inputRest.readOnly
    const { showPicker, handleShowPicker, handleMyColor, myColor, deleteMyColor, ref } = useInputColorLogic({ onChange, defaultValue, value })
    const borderColor = showPicker ? 'border-green-43' : 'border-gray-97'
    const textColor = showPicker ? 'text-green-43' : ''
    const myClassName = myColor ? 'twin_elipsis' : ''
    return (
        <div className={'relative ' + (className || '')}>
            <div className={`flex cursor-pointer relative border-b ${borderColor} pb-8 mt-6 input_color_header items-center`} onClick={readOnly? undefined : handleShowPicker}>
                <CustomRenderColor value={myColor} />
                <span className={`${textColor} ${myClassName}`}>{label}</span>
                {myColor ? <div className='ml-auto cursor-pointer mr-10' onClick={deleteMyColor}><TwinIcon icon={faX} fontSize={12}/></div> : <CustomSelectDropdownIcon opened={showPicker} />}
            </div>
            <InputHidden value={myColor} {...inputRest}/>
            {showPicker ? <div ref={ref} ><TwinColor className='twinColor' color={myColor} onChange={handleMyColor} {...{ colors }}/></div> : null}
        </div>
    )
}
interface InputColorLogicProps {
    defaultValue?: string
    onChange?: (value: string) => void
    value?: string
}

const useInputColorLogic = ({defaultValue, onChange, value}: InputColorLogicProps) => {
    const [showPicker, setShowPicker] = useState<boolean>(false)
    const [myColor, setMyColor] = useState<string>(value || defaultValue || '')
    const ref = useRef<any>(null)

    useOnClickOutside(ref, () => setShowPicker(false))

    const handleShowPicker = useCallback(() => {
        setShowPicker((old)=>!old)
    }, [setShowPicker])

    const handleMyColor = useCallback((color: string) => {
        setMyColor(color)
        onChange?.(color)
    }, [setMyColor, onChange])

    const deleteMyColor: React.MouseEventHandler = useCallback((e) => {
        e.stopPropagation()
        e.preventDefault()
        setMyColor('')
        onChange?.('')
    }, [setMyColor, onChange])

    useEffect(() => {
        if (value) {
            setMyColor(value)
        }
    }, [value])
    return { showPicker, handleShowPicker, handleMyColor, myColor, deleteMyColor, ref }
}


export interface InputColorWithMarginProps extends InputColorProps {}

export const InputColorWithMargin: React.FC<InputColorWithMarginProps> = ({ className, ...res }) => {
    return (<InputColor {...res} />)
}
export interface InputColorDeleteButtonWithMarginProps extends InputColorProps {}

export const InputColorDeleteButtonWithMargin: React.FC<InputColorDeleteButtonWithMarginProps> = ({ className, ...res }) => {
    return (<InputColorWithDeleteButton className={'mt-16 ' + (className || '')} {...res} />)
}

export const InputColorSimple: React.FC<InputColorProps> = ({ className, colors, onChange, defaultValue, value, ...inputRest }) => {
    const readOnly = inputRest.readOnly
    const { showPicker, handleShowPicker, handleMyColor, myColor, ref } = useInputColorLogic({ onChange, defaultValue, value })
    return (
        <div className={'relative ' + (className || '')}>
            <div className='cursor-pointer' onClick={readOnly ? undefined : handleShowPicker}>
                <CustomRenderColor value={myColor} />
            </div>
            <InputHidden value={myColor} {...inputRest} />
            {showPicker ? <div ref={ref} ><TwinColor className='twinColor' color={myColor} onChange={handleMyColor} {...{ colors }} /></div> : null}
        </div>
    )
}

export default InputColor